import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

const fmt = (n) => (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
const fmtMoney = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;

export default function Plans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [my, setMy] = useState(null);
  const [quotes, setQuotes] = useState({});
  const [busy, setBusy] = useState("");
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiry, setEnquiry] = useState({ company_name: "", contact_name: "", email: "", phone: "", monthly_volume: "", message: "" });
  const [enquiryBusy, setEnquiryBusy] = useState(false);
  const [enquiryDone, setEnquiryDone] = useState(false);

  const sendEnquiry = async (e) => {
    e.preventDefault();
    setEnquiryBusy(true);
    try {
      await api.post("/subscriptions/enterprise-enquiry", enquiry);
      setEnquiryDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEnquiryBusy(false);
    }
  };

  useEffect(() => {
    api.get("/subscriptions/plans").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.role === "employer") {
      api.get("/subscriptions/my").then((r) => setMy(r.data.subscription)).catch(() => {});
    }
  }, [user]);
  useEffect(() => {
    if (user?.role === "employer") {
      api.get(`/subscriptions/upgrade-quotes${annual ? "?annual=1" : ""}`)
        .then((r) => setQuotes(r.data.quotes || {})).catch(() => {});
    }
  }, [user, annual]);

  const subscribe = async (planKey) => {
    if (!user) { navigate("/login?next=/plans"); return; }
    if (user.role !== "employer") { toast.error("Plans are for employer accounts."); return; }
    setBusy(planKey);
    try {
      const r = await api.post("/subscriptions/checkout", {
        plan_key: planKey, annual, origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy("");
    }
  };

  const changePlan = async (planKey) => {
    setBusy(planKey);
    try {
      const target = (data?.plans || []).find((p) => p.key === planKey);
      const annualSwitch = annual && !my?.annual && target && !target.custom_only
        && target.monthly_price >= (my?.monthly_price || 0);
      const isUpgrade = !annualSwitch && target && !target.custom_only && target.monthly_price > (my?.monthly_price || 0);
      if ((isUpgrade || annualSwitch) && my?.stripe_subscription_id) {
        // upgrades & annual switches confirm on Stripe's own page, never charge the card silently
        const r = await api.post("/subscriptions/upgrade-checkout", {
          plan_key: planKey, annual: annualSwitch, origin_url: window.location.origin,
        });
        if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
        setMy(r.data.subscription);
        toast.success(annualSwitch
          ? `Annual billing active, ${r.data.subscription.plan_name} now renews once a year.`
          : `You're now on ${r.data.subscription.plan_name}.`);
      } else {
        // downgrades are scheduled for the next renewal, nothing is charged today
        const r = await api.post("/subscriptions/change-plan", {
          plan_key: planKey, annual: false, origin_url: window.location.origin,
        });
        setMy(r.data.subscription);
        const s = r.data.subscription;
        toast.success(s.pending_plan_key
          ? `Switch to ${s.pending_plan_name} scheduled for your next renewal, nothing to pay today.`
          : `You're on ${s.plan_name}.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const discount = data?.annual_discount_pct ?? 5;
  const myActive = my && ["active", "past_due", "cancelling"].includes(my.status);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="plans-page">
      <Seo
        title="Employer Subscription Plans | HiredToday"
        description="Monthly hiring plans for employers who recruit regularly: bundled listings, monthly credits, annual discount."
        path="/plans"
      />
      <div className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">For regular hirers</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">
          Hiring plans that scale with you.
        </h1>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="billing-toggle">
        <button onClick={() => setAnnual(false)} data-testid="billing-monthly"
          className={`h-10 px-4 sm:px-5 whitespace-nowrap rounded-full text-sm font-semibold border transition-colors duration-200 ${
            !annual ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}>
          Monthly billing
        </button>
        <button onClick={() => setAnnual(true)} data-testid="billing-annual"
          className={`h-10 px-4 sm:px-5 whitespace-nowrap rounded-full text-sm font-semibold border transition-colors duration-200 ${
            annual ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}>
          Annual billing
          <span className={`ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded ${annual ? "bg-green-400 text-zinc-950" : "bg-green-100 text-green-700"}`}>
            Save {discount}%
          </span>
        </button>
        <span className="group relative inline-flex" data-testid="billing-info">
          <Info className="h-4 w-4 text-zinc-400 cursor-help" />
          <span className="pointer-events-none absolute right-0 sm:right-auto sm:left-0 top-full z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-md bg-zinc-950 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            Annual plans are billed once a year at the monthly price x 12, less {discount}%. Your listing credits are still released month by month.
          </span>
        </span>
      </div>

      {myActive && (
        <div className="mt-8 max-w-2xl border border-green-200 bg-green-50 rounded-md px-4 py-3 text-sm text-green-900"
          data-testid="current-coverage">
          You're on <strong>{my.plan_name}</strong>, {my.included_jobs} listings every month,
          {" "}{my.monthly_remaining} of {my.credits_total} credits left this month
          {(my.cancel_at_period_end || my.status === "cancelling") ? (
            <> · plan ends {new Date(my.paid_until || my.credit_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}, no further charges</>
          ) : my.paid_until && !my.custom ? (
            <> · renews {new Date(my.paid_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at {fmtMoney(my.monthly_price)}/month</>
          ) : null}.
          {" "}<Link to="/dashboard" className="font-semibold underline underline-offset-2 hover:text-green-950" data-testid="coverage-manage-link">Manage in Company Settings</Link>
        </div>
      )}

      {myActive && my?.pending_plan_key && (
        <div className="mt-8 max-w-2xl border border-amber-200 bg-amber-50 rounded-md px-4 py-3 text-sm text-amber-800"
          data-testid="pending-downgrade-banner">
          Your plan changes to <strong>{my.pending_plan_name}</strong> on{" "}
          {my.pending_plan_effective
            ? new Date(my.pending_plan_effective).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
            : "your next renewal"}, nothing to pay today, and you keep every {my.plan_name} feature until then.
        </div>
      )}

      {!data ? (
        <div className="mt-14 border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {data.plans.map((p, i) => {
            const isMine = myActive && my.plan_key === p.key;
            return (
              <div key={p.key} data-testid={`plan-card-${p.key}`}
                className={`animate-fade-up rounded-md p-8 flex flex-col border ${
                  p.recommended ? "border-blue-300 bg-blue-50/40 border-t-4 border-t-blue-600" : "border-zinc-200 bg-white"}`}
                style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-display text-xl font-bold text-zinc-950">{p.name}</h2>
                  {p.recommended && <span className="text-[11px] font-bold uppercase tracking-wider bg-blue-600 text-white px-2 py-0.5 rounded">Recommended</span>}
                  {isMine && <span className="text-[11px] font-bold uppercase tracking-wider bg-green-100 text-green-700 px-2 py-0.5 rounded" data-testid={`plan-current-${p.key}`}>Your plan</span>}
                </div>
                <p className="text-sm text-zinc-500 mt-1">{p.custom_only ? "For more than 30 listings a month" : `${p.included_jobs} job listings every month`}</p>
                {p.custom_only ? (
                  <>
                    <p className="font-display text-4xl font-black text-zinc-950 mt-6">Custom</p>
                    <p className="text-xs text-zinc-400 mt-1">tailored price, agreed with our team</p>
                  </>
                ) : (
                  <>
                    <p className="font-display text-4xl font-black text-zinc-950 mt-6" data-testid={`plan-price-${p.key}`}>
                      {fmtMoney(annual ? p.annual_price : p.monthly_price)}
                      <span className="text-base font-bold text-zinc-400 whitespace-nowrap">{annual ? " / year" : " / month"}</span>
                    </p>
                    <p className="text-xs text-zinc-400 mt-1" data-testid={`plan-equiv-${p.key}`}>
                      {annual
                        ? `works out at £${fmt(p.annual_price / 12)} a month, billed yearly`
                        : `or £${fmt(p.annual_price)} a year (save ${discount}%)`}
                      {` · about £${fmt(p.per_listing)} per listing`}
                    </p>
                  </>
                )}
                <ul className="mt-7 space-y-3 flex-1">
                  {(p.custom_only ? [
                    "Volume agreed with our team, as many listings as you need",
                    "Your own agreed rate for extra listings",
                    "Featured and Urgent upgrades still available",
                    "Cancel any time, listings stay live until they expire",
                  ] : [
                    `${p.included_jobs} listing credits released monthly`,
                    `Extra listings £${fmt(p.extra_job_price)} each when you run out`,
                    "Featured and Urgent upgrades still available",
                    "Cancel any time, listings stay live until they expire",
                  ]).map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm text-zinc-700">
                      <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${p.recommended ? "text-blue-600" : "text-zinc-400"}`} />{x}
                    </li>
                  ))}
                </ul>
                {myActive && !p.custom_only && (!isMine || (annual && !my?.annual && !my?.pending_plan_key)) && quotes[p.key] && (
                  <p className="mt-5 text-xs font-semibold text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2"
                    data-testid={`plan-quote-${p.key}`}>
                    {annual ? "Switch to annual: " : "Switch today for "}
                    {fmtMoney(quotes[p.key].today)}{annual ? " today" : ""}, then {fmtMoney(quotes[p.key].then)}/{quotes[p.key].per || "month"}
                  </p>
                )}
                {p.custom_only ? (
                  <button onClick={() => { setEnquiryOpen(true); setEnquiryDone(false); }} data-testid={`plan-cta-${p.key}`}
                    className="group mt-8 inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 transition-colors duration-200">
                    Request a custom plan <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                ) : isMine ? (
                  annual && !my?.annual && !my?.pending_plan_key && my?.stripe_subscription_id ? (
                    <button onClick={() => changePlan(p.key)} disabled={!!busy} data-testid={`plan-cta-${p.key}`}
                      className="mt-8 inline-flex items-center justify-center h-12 px-4 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                      {busy === p.key ? "Working…" : "Switch to annual billing"}
                    </button>
                  ) : my?.pending_plan_key ? (
                    <button onClick={() => changePlan(p.key)} disabled={!!busy} data-testid={`plan-cta-${p.key}`}
                      title="Undo the scheduled plan change and stay on your current plan"
                      className="mt-8 inline-flex items-center justify-center h-12 px-4 rounded-md text-sm font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors duration-200 disabled:opacity-50">
                      {busy === p.key ? "Working…" : `Keep ${p.name} (undo)`}
                    </button>
                  ) : (
                    <Link to="/dashboard" data-testid={`plan-cta-${p.key}`}
                      className="mt-8 inline-flex items-center justify-center h-12 rounded-md text-sm font-semibold border border-zinc-300 text-zinc-900 hover:border-zinc-500 transition-colors duration-200">
                      Manage in your dashboard
                    </Link>
                  )
                ) : myActive && my?.pending_plan_key === p.key ? (
                  <button disabled data-testid={`plan-cta-${p.key}`}
                    className="mt-8 inline-flex items-center justify-center h-12 rounded-md text-sm font-semibold border border-zinc-200 bg-zinc-50 text-zinc-500 cursor-default">
                    Starts at your next renewal
                  </button>
                ) : myActive ? (
                  <button onClick={() => changePlan(p.key)} disabled={!!busy} data-testid={`plan-cta-${p.key}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors duration-200 disabled:opacity-50 ${
                      p.recommended ? "bg-zinc-950 text-white hover:bg-zinc-800" : "border border-zinc-300 text-zinc-900 hover:border-zinc-500"}`}>
                    {busy === p.key ? "Switching…" : `Switch to ${p.name}`}
                  </button>
                ) : (
                  <button onClick={() => subscribe(p.key)} disabled={!!busy} data-testid={`plan-cta-${p.key}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors duration-200 disabled:opacity-50 ${
                      p.recommended ? "bg-zinc-950 text-white hover:bg-zinc-800" : "border border-zinc-300 text-zinc-900 hover:border-zinc-500"}`}>
                    {busy === p.key ? "Taking you to checkout…" : `Choose ${p.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-10 text-sm text-zinc-500 max-w-2xl">
        Only need one listing? <Link to="/pricing" className="text-blue-700 font-medium hover:text-blue-900" data-testid="plans-one-off-link">Pay per posting instead</Link> —
        the free, Standard and Featured one-time options are unchanged.
      </p>

      {enquiryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/50" data-testid="enterprise-dialog"
          onClick={() => setEnquiryOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            {enquiryDone ? (
              <div className="text-center py-6" data-testid="enterprise-done">
                <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
                <h2 className="font-display text-xl font-bold text-zinc-950 mt-4">Thank you, that's with our team</h2>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                  We'll be in touch within one working day to shape a plan around your hiring volume.
                </p>
                <button onClick={() => setEnquiryOpen(false)} data-testid="enterprise-done-close"
                  className="mt-6 h-11 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={sendEnquiry}>
                <h2 className="font-display text-xl font-bold text-zinc-950">Request a custom plan</h2>
                <p className="text-sm text-zinc-500 mt-1 mb-5">
                  Tell us a little about your hiring and we'll put together pricing that fits. No commitment.
                </p>
                <div className="space-y-3">
                  <input value={enquiry.company_name} required data-testid="enterprise-company"
                    onChange={(e) => setEnquiry({ ...enquiry, company_name: e.target.value })}
                    placeholder="Company name" className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input value={enquiry.contact_name} required data-testid="enterprise-contact"
                      onChange={(e) => setEnquiry({ ...enquiry, contact_name: e.target.value })}
                      placeholder="Your name" className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    <input type="email" value={enquiry.email} required data-testid="enterprise-email"
                      onChange={(e) => setEnquiry({ ...enquiry, email: e.target.value })}
                      placeholder="Work email" className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input value={enquiry.phone} data-testid="enterprise-phone"
                      onChange={(e) => setEnquiry({ ...enquiry, phone: e.target.value })}
                      placeholder="Phone (optional)" className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    <input value={enquiry.monthly_volume} data-testid="enterprise-volume"
                      onChange={(e) => setEnquiry({ ...enquiry, monthly_volume: e.target.value })}
                      placeholder="Jobs per month (e.g. 40)" className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <textarea value={enquiry.message} rows={3} data-testid="enterprise-message"
                    onChange={(e) => setEnquiry({ ...enquiry, message: e.target.value })}
                    placeholder="Anything else we should know? (optional)"
                    className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div className="flex gap-2 mt-6">
                  <button type="submit" disabled={enquiryBusy} data-testid="enterprise-submit"
                    className="flex-1 h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                    {enquiryBusy ? "Sending…" : "Send enquiry"}
                  </button>
                  <button type="button" onClick={() => setEnquiryOpen(false)} data-testid="enterprise-cancel"
                    className="h-11 px-5 border border-zinc-300 text-sm font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                    Not now
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
